import axios from 'axios';

const API_BASE_URL = 'https://billing.clafoutis.eu/api';

/**
 * Service pour gérer les appels API Support WHMCS
 */
class SupportService {
  constructor(getTokenFn) {
    this.getToken = getTokenFn;
  }

  /**
   * Crée une instance axios avec le token d'authentification
   */
  async getHttpClient() {
    const token = await this.getToken();
    return axios.create({
      baseURL: API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  }

  // ============================================
  // TICKETS
  // ============================================

  /**
   * Lister les tickets
   * @param {Object} params - Paramètres de filtrage (status, clientId, departmentId, limitnum, limitstart)
   */
  async getTickets(params = {}) {
    const http = await this.getHttpClient();
    const queryParams = new URLSearchParams(params).toString();
    const response = await http.get(`/support/tickets${queryParams ? '?' + queryParams : ''}`);
    return response.data;
  }

  /**
   * Récupérer un ticket avec son contexte complet
   * @param {string} ticketId - ID du ticket
   */
  async getTicket(ticketId) {
    const http = await this.getHttpClient();
    const response = await http.get(`/support/tickets/${ticketId}`);
    return response.data;
  }

  /**
   * Répondre à un ticket
   * @param {string} ticketId - ID du ticket
   * @param {Object} data - { message, status, markdown }
   */
  async replyToTicket(ticketId, data) {
    const http = await this.getHttpClient();
    const response = await http.post(`/support/tickets/${ticketId}/reply`, data);
    return response.data;
  }

  /**
   * Changer le statut d'un ticket
   * @param {string} ticketId - ID du ticket
   * @param {string} status - Nouveau statut (Open, Answered, Customer-Reply, Closed, On Hold, In Progress)
   */
  async updateTicketStatus(ticketId, status) {
    const http = await this.getHttpClient();
    const response = await http.patch(`/support/tickets/${ticketId}/status`, { status });
    return response.data;
  }

  /**
   * Récupérer le contexte client complet
   * @param {string} ticketId - ID du ticket
   */
  async getClientContext(ticketId) {
    const http = await this.getHttpClient();
    const response = await http.get(`/support/tickets/${ticketId}/context`);
    return response.data;
  }

  /**
   * Lister les départements de support
   */
  async getDepartments() {
    const http = await this.getHttpClient();
    const response = await http.get('/support/tickets/departments');
    return response.data;
  }

  /**
   * Lister les statuts disponibles
   */
  async getStatuses() {
    const http = await this.getHttpClient();
    const response = await http.get('/support/tickets/statuses');
    return response.data;
  }

  // ============================================
  // IA
  // ============================================

  /**
   * Obtenir une suggestion de réponse automatique avec IA
   * @param {string} ticketId - ID du ticket
   * @param {boolean} includeKnowledgeBase - Inclure les suggestions de base de connaissances
   */
  async getSuggestedResponse(ticketId, includeKnowledgeBase = true) {
    const http = await this.getHttpClient();
    const response = await http.post(`/support/tickets/${ticketId}/ai/suggest-response`, {
      include_knowledge_base: includeKnowledgeBase
    });
    return response.data;
  }

  /**
   * Améliorer une réponse avec l'IA
   * @param {string} ticketId - ID du ticket
   * @param {string} responseText - Texte de la réponse à améliorer
   */
  async improveResponse(ticketId, responseText) {
    const http = await this.getHttpClient();
    const response = await http.post(`/support/tickets/${ticketId}/ai/improve-response`, {
      response: responseText
    });
    return response.data;
  }

  /**
   * Traduire et améliorer une réponse (auto-détection de la langue)
   * @param {string} ticketId - ID du ticket
   * @param {string} message - Message à traduire
   * @param {string} targetLanguage - Langue cible (optionnel, auto-détection sinon)
   */
  async translateAndImprove(ticketId, message, targetLanguage = null) {
    const http = await this.getHttpClient();
    const data = { message };
    if (targetLanguage) {
      data.target_language = targetLanguage;
    }
    const response = await http.post(`/support/tickets/${ticketId}/ai/translate-and-improve`, data);
    return response.data;
  }

  /**
   * Obtenir un résumé du contexte client généré par l'IA
   * @param {string} ticketId - ID du ticket
   */
  async getContextSummary(ticketId) {
    const http = await this.getHttpClient();
    const response = await http.get(`/support/tickets/${ticketId}/ai/context-summary`);
    return response.data;
  }

  /**
   * Obtenir les suggestions de la base de connaissances
   * @param {string} ticketId - ID du ticket
   */
  async getSuggestedKBResponses(ticketId) {
    const http = await this.getHttpClient();
    const response = await http.get(`/support/tickets/${ticketId}/ai/suggested-kb-responses`);
    return response.data;
  }

  // ============================================
  // BASE DE CONNAISSANCES
  // ============================================

  /**
   * Lister les réponses de la base de connaissances
   * @param {Object} params - { product_type, category, search, include_inactive, per_page }
   */
  async getKBResponses(params = {}) {
    const http = await this.getHttpClient();
    const queryParams = new URLSearchParams(params).toString();
    const response = await http.get(`/knowledge-base${queryParams ? '?' + queryParams : ''}`);
    return response.data;
  }

  /**
   * Récupérer une réponse de la base de connaissances
   * @param {number} id - ID de la réponse
   */
  async getKBResponse(id) {
    const http = await this.getHttpClient();
    const response = await http.get(`/knowledge-base/${id}`);
    return response.data;
  }

  /**
   * Créer une réponse dans la base de connaissances
   * @param {Object} data - { product_type, category, title, content, keywords, priority, is_active }
   */
  async createKBResponse(data) {
    const http = await this.getHttpClient();
    const response = await http.post('/knowledge-base', data);
    return response.data;
  }

  /**
   * Modifier une réponse de la base de connaissances
   * @param {number} id - ID de la réponse
   * @param {Object} data - Champs à modifier
   */
  async updateKBResponse(id, data) {
    const http = await this.getHttpClient();
    const response = await http.put(`/knowledge-base/${id}`, data);
    return response.data;
  }

  /**
   * Supprimer une réponse de la base de connaissances
   * @param {number} id - ID de la réponse
   */
  async deleteKBResponse(id) {
    const http = await this.getHttpClient();
    const response = await http.delete(`/knowledge-base/${id}`);
    return response.data;
  }

  /**
   * Lister les catégories disponibles
   */
  async getKBCategories() {
    const http = await this.getHttpClient();
    const response = await http.get('/knowledge-base/categories');
    return response.data;
  }

  /**
   * Lister les types de produits
   */
  async getKBProductTypes() {
    const http = await this.getHttpClient();
    const response = await http.get('/knowledge-base/product-types');
    return response.data;
  }

  /**
   * Lister les départements de la KB
   */
  async getKBDepartments() {
    const http = await this.getHttpClient();
    const response = await http.get('/knowledge-base/departments');
    return response.data;
  }
}

/**
 * Factory function pour créer une instance de SupportService
 * @param {Function} getTokenFn - Fonction pour obtenir le token d'authentification
 */
export function createSupportService(getTokenFn) {
  return new SupportService(getTokenFn);
}

export default SupportService;
